/**
 * useIdleLogout — signs the user out after 12 hours of no interaction.
 *
 *   • Any of mousemove / keydown / mousedown / touchstart / scroll / visibilitychange
 *     counts as activity. Updates are throttled to once every 30s so
 *     mousemove floods don't hammer localStorage.
 *
 *   • Multi-tab safe: activity timestamp lives in localStorage under
 *     `sp_last_activity_ms`. All tabs listen to the `storage` event so
 *     activity in one tab keeps every tab alive.
 *
 *   • Shows a 60-second countdown modal before auto-signout so the user
 *     can tap "Stay signed in" to keep working.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const KEY = "sp_last_activity_ms";
// 30-day safety-net idle timeout for regular users. Super-admin /
// editor sessions are excluded from this hook entirely (see the
// `enabled` prop wired from IdleLogout.jsx). Was 12h before 2026-09
// when several long-tenured admins reported being logged out mid-day.
const IDLE_MS = 30 * 24 * 60 * 60 * 1000;      // 30 days
const WARN_MS = 60 * 1000;                     // 1-minute warning
const THROTTLE_MS = 30 * 1000;                 // update lastActivity at most once / 30s
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];

function readLast() {
  try { return parseInt(localStorage.getItem(KEY) || "0", 10) || 0; } catch { return 0; }
}
function writeLast(v) { try { localStorage.setItem(KEY, String(v)); } catch { /* ignore */ } }

export function useIdleLogout({ enabled, onLogout }) {
  const [warnRemaining, setWarnRemaining] = useState(0);   // seconds shown in modal, 0 = hidden
  const lastWriteRef = useRef(0);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  // Reset activity timestamp NOW (called on user action + when the user
  // clicks "Stay signed in" in the warning modal).
  const bump = useCallback((force = false) => {
    const now = Date.now();
    if (!force && now - lastWriteRef.current < THROTTLE_MS) return;
    lastWriteRef.current = now;
    writeLast(now);
  }, []);

  const dismissWarning = useCallback(() => {
    setWarnRemaining(0);
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    bump(true);
  }, [bump]);

  useEffect(() => {
    if (!enabled) return;

    // Seed on first mount.
    if (readLast() === 0) writeLast(Date.now());

    const onActivity = () => bump(false);
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true });

    // Cross-tab: another tab bumped activity? Reset our timers too.
    const onStorage = (e) => { if (e.key === KEY) scheduleNextCheck(); };
    window.addEventListener("storage", onStorage);
    const onVis = () => { if (document.visibilityState === "visible") scheduleNextCheck(); };
    document.addEventListener("visibilitychange", onVis);

    const stopAllTimers = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    };

    const scheduleNextCheck = () => {
      stopAllTimers();
      const last = readLast() || Date.now();
      const elapsed = Date.now() - last;
      // Already past the idle deadline? Log out immediately.
      if (elapsed >= IDLE_MS) { setWarnRemaining(0); onLogout?.("idle"); return; }
      const untilWarn = IDLE_MS - WARN_MS - elapsed;
      if (untilWarn > 0) {
        // Wake up when the 60-second warning is due.
        timerRef.current = setTimeout(() => {
          setWarnRemaining(Math.ceil(WARN_MS / 1000));
          countdownRef.current = setInterval(() => {
            const lastNow = readLast() || Date.now();
            const remaining = IDLE_MS - (Date.now() - lastNow);
            if (remaining <= 0) {
              stopAllTimers();
              setWarnRemaining(0);
              onLogout?.("idle");
            } else if (remaining > WARN_MS) {
              // User was active in another tab → cancel warning.
              stopAllTimers();
              setWarnRemaining(0);
              scheduleNextCheck();
            } else {
              setWarnRemaining(Math.ceil(remaining / 1000));
            }
          }, 1000);
        }, untilWarn);
      } else {
        // Already inside the warn window.
        setWarnRemaining(Math.ceil((IDLE_MS - elapsed) / 1000));
        countdownRef.current = setInterval(() => {
          const lastNow = readLast() || Date.now();
          const remaining = IDLE_MS - (Date.now() - lastNow);
          if (remaining <= 0) { stopAllTimers(); setWarnRemaining(0); onLogout?.("idle"); }
          else if (remaining > WARN_MS) { stopAllTimers(); setWarnRemaining(0); scheduleNextCheck(); }
          else { setWarnRemaining(Math.ceil(remaining / 1000)); }
        }, 1000);
      }
    };

    scheduleNextCheck();

    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVis);
      stopAllTimers();
    };
  }, [enabled, onLogout, bump]);

  return { warnRemaining, dismissWarning };
}
